import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";
import TenantContext from "../../context/tenant/TenantContext";
import JoinApplicationScreen, {
  JoinAs,
} from "../../components/JoinApplicationScreen/JoinApplicationScreen";
import SharedWithLandlordScreen, {
  SharedMember,
} from "../../components/SharedWithLandlordScreen/SharedWithLandlordScreen";
import GuarantorInviteSection, {
  GuarantorInvite,
} from "../../components/GuarantorInviteSection/GuarantorInviteSection";
import TenantInviteSection, {
  TenantInvite,
} from "../../components/TenantInviteSection/TenantInviteSection";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import SkorProtectShield from "../../assets/img/skor-protect-shield.png";
import {
  isClientIdentificationActive,
  haveValue,
  toast,
} from "../../helper/common";
import { sendUniversalMail } from "../../services/sharedService";
import useTranslator from "../../hooks/useTranslator";
import styles from "./CreateApplicationStep.module.css";
import SelfManagedGuarantorsModal from "../../components/SelfManagedGuarantorsModal/SelfManagedGuarantorsModal";
import SelfManagedCoTenantsModal from "../../components/SelfManagedCoTenantsModal/SelfManagedCoTenantsModal";
import { ApplicationMember } from "./ApplicationPeopleSummary";
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Icon,
  TextArea,
  TextField,
} from "../../components/design";
import InvitationShareModal from "../../components/InvitationShareModal";
import ConsentStepModal from "./ConsentStepModal";
import {
  clearSignupPersistedState,
  getLandlordLinkParams,
  getSharedLandlordNames,
  getSignupApplicationId,
  getSignupEntrySource,
  isAppSharedWithLandlordId,
} from "../../components/Onboarding/signupEntrySource";

type CreatedAppPayload = {
  title: string;
  peopleCount: number;
  appId?: string;
  members?: ApplicationMember[];
  pendingGuarantors?: { name: string; email?: string; relation?: string }[];
  pendingCoTenants?: { name: string; email?: string }[];
};

type CreateApplicationStepProps = {
  onBack: () => void;
  updateTenantData: any;
  onCreated?: (payload: CreatedAppPayload) => void;
};

const CreateApplicationStep = ({
  onBack,
  updateTenantData,
  onCreated,
}: CreateApplicationStepProps) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const {
    createApplication,
    consentApplication,
    pendingApplications,
    myApplications,
    landlordRequests,
    loadApplicationsData,
    loadLandlordRequests,
    applicationsLoaded,
    acceptLandlordRequest,
    openLandlordLink,
  } = useContext(ApplicationContext);
  const { user, loadUserTenant } = useContext(AuthContext);
  const {
    createBasicGuarantorAccount,
    upsertSelectedOnboardingUsers,
    upsertSelectedCoTenants,
  } = useContext(TenantContext);
  const language = (localStorage.getItem("i18nextLng") || "en").toLowerCase();
  const defaultTitle = useMemo(
    () =>
      language.startsWith("es")
        ? `Aplicación de ${user?.fname || ""}`
        : `${user?.fname || ""}'s application`,
    [language, user?.fname]
  );
  const [title, setTitle] = useState(defaultTitle);
  const [editingTitle, setEditingTitle] = useState(false);
  const [description, setDescription] = useState(user?.about_me || "");
  const [showNote, setShowNote] = useState(true);
  const [tenantInvites, setTenantInvites] = useState<TenantInvite[]>([]);
  const [guarantorInvites, setGuarantorInvites] = useState<GuarantorInvite[]>(
    []
  );
  const [selectedOnboardingUsers, setSelectedOnboardingUsers] = useState<any[]>(
    () =>
      Array.isArray(user?.tenant_extra_field_id?.selected_onboarding_users)
        ? user.tenant_extra_field_id.selected_onboarding_users
        : []
  );
  const [selectedCoTenants, setSelectedCoTenants] = useState<any[]>(() =>
    Array.isArray(user?.tenant_extra_field_id?.selected_co_tenants)
      ? user.tenant_extra_field_id.selected_co_tenants
      : []
  );
  const [skorProtect, setSkorProtect] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [initializationError, setInitializationError] = useState(false);
  const [initializationAttempt, setInitializationAttempt] = useState(0);
  const [creating, setCreating] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [showPendingOnRefresh, setShowPendingOnRefresh] = useState(false);
  // Website-entry orchestrator for cases 1-4 (flatmate decision -> create -> landlord choice).
  // Flatmate-link entry (cases 5-7) uses the same queue/share/success
  // machinery but with Finish-only B-prime (no Decline – the link itself
  // carries consent). Landlord-link entry (cases 8-11) reuses the same
  // machinery with accept/decline decision: the link is registered via
  // openLandlordLink into the landlord-request queue, per-request auto-share
  // applies when the app is already shared with that landlord (cases 10/11),
  // and closing the choice shares (link consent) instead of sharing nothing.
  const [entrySource] = useState(() => getSignupEntrySource());
  const useWebsiteFlow = entrySource === "website";
  const useFlatmateLinkFlow = entrySource === "flatmate-link";
  const useLandlordLinkFlow = entrySource === "landlord-link";
  const useInvitationOrchestrator =
    useWebsiteFlow || useFlatmateLinkFlow || useLandlordLinkFlow;
  const flowTag = "[signup-flow]";
  useEffect(() => {
    console.log(flowTag, "entry detected", {
      entrySource,
      useWebsiteFlow,
      useFlatmateLinkFlow,
      useLandlordLinkFlow,
      search: window.location.search,
    });
  }, [entrySource, useFlatmateLinkFlow, useLandlordLinkFlow, useWebsiteFlow]);
  const [landlordQueue, setLandlordQueue] = useState<any[]>([]);
  const [landlordIndex, setLandlordIndex] = useState(0);
  // Set when the client-identification form is submitted for a queue share.
  // Consumed by handleLandlordShared to decide on a user reload.
  const viviaSubmittedRef = useRef(false);
  const [consentLandlordData, setConsentLandlordData] = useState<any>(null);
  const postConsentActionRef = useRef<(() => Promise<void>) | null>(null);
  const [resolvedApp, setResolvedApp] = useState<{
    id?: string;
    title: string;
    participants?: any[];
    // Email of the application creator for lead-tenant detection on the
    // shared success screen (participants carry is_lead from the API, but
    // hand-built create-flow participants do not).
    leadEmail?: string;
  } | null>(null);
  const [declinedFlatmate, setDeclinedFlatmate] = useState(false);
  const [returnToLandlordChoice, setReturnToLandlordChoice] = useState(false);
  const QUEUE_STORAGE_KEY = "signupLandlordQueueState";
  const persistQueueState = (
    queue: any[],
    index: number,
    resolved: {
      id?: string;
      title: string;
      participants?: any[];
      leadEmail?: string;
    } | null,
    declined: boolean
  ) => {
    try {
      sessionStorage.setItem(
        QUEUE_STORAGE_KEY,
        JSON.stringify({
          requestIds: (queue ?? [])
            .slice(index)
            .map((r: any) => r?._id)
            .filter(Boolean),
          index: 0,
          resolvedApp: resolved,
          declinedFlatmate: declined,
        })
      );
    } catch { }
  };
  const clearQueueState = () => {
    try {
      sessionStorage.removeItem(QUEUE_STORAGE_KEY);
    } catch { }
  };
  // Joined-application success (case 2 accept): the person joined an existing
  // application instead of creating one. Persisted across remounts/reloads.
  const [joinedResult, setJoinedResult] = useState<{
    title: string;
    sharedLandlords: string[];
  } | null>(() => {
    try {
      const raw = sessionStorage.getItem("signupJoinedResult");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  // Shared-with-landlord success (cases 3-4 after sharing): confirms which
  // landlords can now review the application. Persisted so the reload after
  // sharing never drops back to home without showing it.
  const [sharedResult, setSharedResult] = useState<{
    landlordNames: string[];
    tenantLabel: string;
    tenantEmail: string;
    // Null for individual-profile shares (no application): those keep the
    // older simple layout, while application shares use the member layout.
    appTitle: string | null;
    members: SharedMember[];
  } | null>(() => {
    try {
      const raw = sessionStorage.getItem("signupSharedResult");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [sharedLandlordNames, setSharedLandlordNames] = useState<string[]>(
    () => {
      try {
        const raw = sessionStorage.getItem("signupSharedResult");
        const parsed = raw ? JSON.parse(raw) : null;
        return Array.isArray(parsed?.landlordNames) ? parsed.landlordNames : [];
      } catch {
        return [];
      }
    }
  );
  const persistSharedResult = (value: {
    landlordNames: string[];
    tenantLabel: string;
    tenantEmail: string;
    appTitle: string | null;
    members: SharedMember[];
  }) => {
    try {
      sessionStorage.setItem("signupSharedResult", JSON.stringify(value));
    } catch { }
    setSharedResult(value);
    setSharedLandlordNames(value.landlordNames);
  };
  const clearSharedResult = () => {
    try {
      sessionStorage.removeItem("signupSharedResult");
    } catch { }
    setSharedResult(null);
    setSharedLandlordNames([]);
  };
  const persistJoinedResult = (value: {
    title: string;
    sharedLandlords: string[];
  }) => {
    try {
      sessionStorage.setItem("signupJoinedResult", JSON.stringify(value));
    } catch { }
    setJoinedResult(value);
  };
  const clearJoinedResult = () => {
    try {
      sessionStorage.removeItem("signupJoinedResult");
    } catch { }
    setJoinedResult(null);
  };
  const goToDashboardFromSignup = (reason: string, extra?: any) => {
    console.log(flowTag, reason, extra ?? {});
    clearSignupPersistedState();
    setJoinedResult(null);
    setSharedResult(null);
    setSharedLandlordNames([]);
    setLandlordQueue([]);
    setLandlordIndex(0);
    setReturnToLandlordChoice(false);
    navigate("/home?welcome=true");
  };
  // Completing the profile reloads the user and remounts this step. Every
  // state transition (landlord queue / join success) must be persisted
  // synchronously BEFORE this call, or the remount initializes from empty
  // storage and falls back to the create form. State setters that show the
  // transition (setLandlordQueue etc.) must run AFTER this call: the remount
  // restores them from storage, and setting them before opened the landlord
  // choice modal twice (once before, once after the remount). On failure the
  // just-persisted transition rolls back and the caller's catch toasts.
  const completeProfile = async (payload: any, rollback?: () => void) => {
    try {
      await updateTenantData(payload);
    } catch (err) {
      try {
        rollback?.();
      } catch { }
      throw err;
    }
  };
  useEffect(() => {
    if (joinedResult) {
      console.log(flowTag, "join success shown", {
        title: joinedResult.title,
        sharedLandlords: joinedResult.sharedLandlords,
      });
    }
  }, [joinedResult]);
  useEffect(() => {
    if (sharedResult) {
      console.log(flowTag, "shared success shown", {
        landlordNames: sharedResult.landlordNames,
        appTitle: sharedResult.appTitle ?? null,
        memberCount: (sharedResult.members ?? []).length,
      });
    }
  }, [sharedResult]);
  const submittingRef = useRef(false);
  const titleChangedRef = useRef(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    setInitializing(true);
    setInitializationError(false);
    (async () => {
      try {
        const { link, propertyId } = getLandlordLinkParams();
        // Register before fetching the queue so an older, empty response
        // cannot overwrite the request created by this link.
        if (useLandlordLinkFlow && link) {
          await openLandlordLink(link, propertyId);
        }
        if (!active) return;
        await Promise.all([
          loadApplicationsData(),
          loadLandlordRequests({ throwOnError: useLandlordLinkFlow }),
        ]);
        if (!active) return;
        // Keep the link available for retry until the queue is loaded.
        if (useLandlordLinkFlow && link) {
          for (const name of [
            "requestProfileLink",
            "property_id",
            "invitationToken",
            "tenantSignupFlow",
          ]) {
            document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
          }
        }
      } catch {
        if (active) setInitializationError(true);
      } finally {
        if (active) setInitializing(false);
      }
    })();
    return () => {
      active = false;
    };
    // Context functions are recreated on updates; retry only on request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLandlordLinkFlow, initializationAttempt]);

  useEffect(() => {
    console.log(flowTag, "landlord requests loaded", {
      count: (landlordRequests ?? []).length,
      requests: (landlordRequests ?? []).map((r: any) => ({
        id: r?._id,
        landlord:
          `${r?.landlord_id?.fname_companyName ?? ""} ${r?.landlord_id?.lname ?? ""}`.trim() ||
          r?.landlord_id?.email ||
          null,
        email: r?.email ?? null,
      })),
    });
  }, [landlordRequests]);

  useEffect(() => {
    if (!titleChangedRef.current) setTitle(defaultTitle);
  }, [defaultTitle]);

  // Restore an in-progress landlord queue after the reload/remount that
  // follows profile completion. Without this the modal never reappears and
  // the create form shows again.
  useLayoutEffect(() => {
    if (!useInvitationOrchestrator) return;
    if (landlordQueue.length > 0) return;
    if (!landlordRequests || landlordRequests.length === 0) return;
    try {
      const raw = sessionStorage.getItem(QUEUE_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const ids: string[] = Array.isArray(parsed?.requestIds)
        ? parsed.requestIds
        : [];
      if (ids.length === 0) return;
      const byId = new Map(
        (landlordRequests ?? []).map((r: any) => [r?._id, r])
      );
      // Slice before matching so older saved queues also preserve the current request.
      const remainingIds = ids.slice(Math.max(parsed?.index ?? 0, 0));
      const matched = remainingIds.map((id) => byId.get(id)).filter(Boolean);
      if (matched.length === 0) {
        clearQueueState();
        return;
      }
      console.log(flowTag, "landlord queue restored after remount", {
        requestIds: ids,
        index: parsed?.index ?? 0,
      });
      setResolvedApp(parsed?.resolvedApp ?? null);
      setDeclinedFlatmate(!!parsed?.declinedFlatmate);
      setReturnToLandlordChoice(false);
      setLandlordQueue(matched);
      setLandlordIndex(0);
    } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landlordRequests, useInvitationOrchestrator]);

  useLayoutEffect(() => {
    // Consent refreshes the lists before the rest of the transition finishes.
    // Keep the current invitation visible until its destination is ready.
    if (processingInvitation) return;
    const sorted = [...(pendingApplications ?? [])].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    // Flatmate invite links now carry the exact application id – pre-select
    // that application instead of guessing the latest pending one. Falls back
    // to latest for old links without the id.
    const linkedId = useFlatmateLinkFlow ? getSignupApplicationId() : null;
    const linked = linkedId
      ? (sorted.find((a: any) => a?._id?.toString?.() === linkedId) ?? null)
      : null;
    // Recover an accepted invite after an interruption between consent and
    // profile completion. Only the exact linked application may be resumed.
    const joined =
      linkedId &&
        !joinedResult &&
        landlordQueue.length === 0 &&
        !returnToLandlordChoice
        ? ((myApplications ?? []).find(
          (a: any) => a?._id?.toString?.() === linkedId
        ) ?? null)
        : null;
    const latest = linkedId ? (linked ?? joined) : (sorted[0] ?? null);
    setSelectedApplication(latest);
    console.log(flowTag, "pending applications loaded", {
      count: (pendingApplications ?? []).length,
      latestId: latest?._id ?? null,
      latestTitle: latest?.title ?? null,
      linkedAppId: linkedId,
      linkedMatched: Boolean(linked),
      sharedWith: latest ? getSharedLandlordNames(latest) : [],
      useWebsiteFlow,
      useFlatmateLinkFlow,
      useLandlordLinkFlow,
    });
  }, [
    pendingApplications,
    myApplications,
    processingInvitation,
    useFlatmateLinkFlow,
    joinedResult,
    landlordQueue.length,
    returnToLandlordChoice,
  ]);

  useEffect(() => {
    const savedUsers = user?.tenant_extra_field_id?.selected_onboarding_users;
    if (Array.isArray(savedUsers)) setSelectedOnboardingUsers(savedUsers);
  }, [user?._id, user?.tenant_extra_field_id?.selected_onboarding_users]);

  useEffect(() => {
    const savedCo = user?.tenant_extra_field_id?.selected_co_tenants;
    if (Array.isArray(savedCo)) setSelectedCoTenants(savedCo);
  }, [user?._id, user?.tenant_extra_field_id?.selected_co_tenants]);

  useEffect(() => {
    if (!user?.profile_completed) return;
    const selected = (
      user?.tenant_extra_field_id?.selected_onboarding_users ?? []
    ).filter((u: any) => u?.type === "guarantor");
    if (selected.length === 0) return;
    const added = user?.gurantor_added_id ?? [];
    const completedUsers: any[] =
      user?.tenant_extra_field_id?.completed_onboarding_users ?? [];
    const normEmail = (v: any) =>
      typeof v === "string" ? v.toLowerCase() : "";
    const normName = (v: any) =>
      typeof v === "string" ? v.trim().toLowerCase() : "";
    const isCompleted = (s: any) => {
      const email = normEmail(s.email);
      if (email) {
        if (
          completedUsers.some(
            (c: any) => c?.type === "guarantor" && normEmail(c?.email) === email
          )
        )
          return true;
        if (
          added.some(
            (a: any) =>
              normEmail(a?.gurantor_email) === email ||
              normEmail(a?.guarantor_id?.email) === email
          )
        )
          return true;
      }
      const name = normName(s.name);
      if (name) {
        if (
          completedUsers.some(
            (c: any) => c?.type === "guarantor" && normName(c?.name) === name
          )
        )
          return true;
        if (
          added.some(
            (a: any) =>
              normName(
                `${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`.trim()
              ) === name
          )
        )
          return true;
      }
      return false;
    };
    const pending = selected.filter((s: any) => !isCompleted(s));
    if (pending.length > 0) setShowPendingOnRefresh(true);
  }, [
    user?.profile_completed,
    user?.tenant_extra_field_id?.selected_onboarding_users,
    user?.gurantor_added_id,
  ]);

  // Co-tenant pending check for self-managed
  const [showCoTenantPending, setShowCoTenantPending] = useState(false);
  useEffect(() => {
    if (!user?.profile_completed) return;
    const selected = (
      user?.tenant_extra_field_id?.selected_co_tenants ?? []
    ).filter((u: any) => u?.type === "co_tenant");
    if (selected.length === 0) return;
    const completedUsers: any[] =
      user?.tenant_extra_field_id?.completed_co_tenants ?? [];
    const normEmail = (v: any) =>
      typeof v === "string" ? v.toLowerCase() : "";
    const normName = (v: any) =>
      typeof v === "string" ? v.trim().toLowerCase() : "";
    const isCompleted = (s: any) => {
      const email = normEmail(s.email);
      if (
        email &&
        completedUsers.some((c: any) => normEmail(c?.email) === email)
      )
        return true;
      const name = normName(s.name);
      if (name && completedUsers.some((c: any) => normName(c?.name) === name))
        return true;
      return false;
    };
    const pending = selected.filter((s: any) => !isCompleted(s));
    if (pending.length > 0) setShowCoTenantPending(true);
  }, [
    user?.profile_completed,
    user?.tenant_extra_field_id?.selected_co_tenants,
    user?.tenant_extra_field_id?.completed_co_tenants,
  ]);

  const existingGuarantors = user?.gurantor_added_id ?? [];
  const peopleCount =
    1 +
    tenantInvites.length +
    guarantorInvites.length +
    existingGuarantors.length;

  // applicationId: the guarantors also join this application (the backend
  // needs the tenant to be an active member of it, so call after accepting)
  const submitGuarantors = async (
    invitesToSubmit: GuarantorInvite[],
    applicationId?: string
  ) => {
    const emailInvites = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod !== "self"
    );
    const selfManagedGuarantors = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod === "self"
    );

    for (const guarantor of emailInvites) {
      const [fname, ...surname] = guarantor.name.split(/\s+/);
      try {
        await createBasicGuarantorAccount({
          fname,
          lname: surname.join(" "),
          email: guarantor.email,
          gurantor_relation: guarantor.relation,
          step: 1,
          ...(applicationId ? { application_id: applicationId } : {}),
        });
      } catch (err: any) {
        if (err?.response?.data?.errorKey !== "account-already-present") {
          throw err;
        }
      }

      await sendUniversalMail({
        receiverEmail: guarantor.email,
        senderId: localStorage.getItem("saveduserid"),
        emailType: "score",
        status: "processing",
        gurantor_relation: guarantor.relation,
        ...(applicationId ? { application_id: applicationId } : {}),
      });
    }

    if (selfManagedGuarantors.length > 0) {
      const newSelectedUsers = selfManagedGuarantors
        .map(({ name, email, relation }) => ({
          name,
          type: "guarantor",
          email,
          gurantor_relation: relation,
        }))
        .filter(
          ({ name, type }) =>
            !selectedOnboardingUsers.some(
              (savedUser) =>
                savedUser?.type === type && savedUser?.name === name
            )
        );
      const nextSelectedUsers = [
        ...selectedOnboardingUsers,
        ...newSelectedUsers,
      ];
      const result = await upsertSelectedOnboardingUsers({
        tenant_id: user?._id ?? localStorage.getItem("saveduserid"),
        users: nextSelectedUsers,
      });
      if (result === null) throw new Error("failed-to-add-guarantor");
      setSelectedOnboardingUsers(nextSelectedUsers);
    }
  };

  const submitCoTenants = async (invitesToSubmit: TenantInvite[]) => {
    const selfManaged = invitesToSubmit.filter(
      ({ tenantMethod }) => tenantMethod === "self"
    );
    if (selfManaged.length === 0) return;
    const newSelected = selfManaged
      .map(({ name, email }) => ({ name, type: "co_tenant", email }))
      .filter(
        ({ name, type }) =>
          !selectedCoTenants.some(
            (saved: any) => saved?.type === type && saved?.name === name
          )
      );
    const next = [...selectedCoTenants, ...newSelected];
    const result = await upsertSelectedCoTenants({
      tenant_id: user?._id ?? localStorage.getItem("saveduserid"),
      users: next,
    });
    if (result === null) throw new Error("failed-to-add-co-tenant");
    setSelectedCoTenants(next);
  };

  const handleCreate = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setCreating(true);

    try {
      const isIndividually = tenantInvites.length === 0;
      const tenantUpdatePayload: any = {
        about_me: description,
        skor_protect: skorProtect,
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      };
      if (isIndividually) {
        tenantUpdatePayload.application_title = title.trim() || defaultTitle;
      }
      await submitCoTenants(tenantInvites);
      // Always create the application, also when the tenant applies alone.
      // Guarantors already on the account join it as their guarantors.
      const res = await createApplication({
        participants: tenantInvites.map(
          ({ name: inviteName, email: inviteEmail, tenantMethod }) => ({
            name: inviteName,
            email: inviteEmail,
            selfManaged: tenantMethod === "self",
          })
        ),
        title: title.trim() || defaultTitle,
        skor_protect: skorProtect,
        description: description.trim(),
        guarantor_ids: existingGuarantors
          .filter(
            (guarantor: any) =>
              guarantor?._id && guarantor?.status !== "rejected"
          )
          .map((guarantor: any) => guarantor._id),
      });
      const createdAppId: string | undefined =
        res?.success_res?._id ?? res?._id ?? res?.success_res?.id ?? undefined;
      // New guarantors join the created application. Called after it exists
      // so a failure here doesn't create the application twice on retry.
      if (createdAppId) {
        await submitJoinGuarantors(guarantorInvites, createdAppId);
      } else {
        await submitGuarantors(guarantorInvites);
      }
      const createdTitle = title.trim() || defaultTitle;
      const pendingGuarantors = guarantorInvites
        .filter((g) => g.guarantorMethod === "self")
        .map((g) => ({
          name: g.name,
          email: g.email,
          relation: g.relation,
        }));
      // also include previously selected self-managed already stored
      const existingPending = selectedOnboardingUsers
        .filter((u: any) => u?.type === "guarantor")
        .map((u: any) => ({
          name: u.name,
          email: u.email,
          relation: u.gurantor_relation ?? u.relation,
        }));
      const allPending = [
        ...existingPending,
        ...pendingGuarantors.filter(
          (p) =>
            !existingPending.some((e) =>
              e.email
                ? e.email.toLowerCase() === p.email.toLowerCase()
                : e.name?.trim().toLowerCase() === p.name.trim().toLowerCase()
            )
        ),
      ];
      const pendingCoTenants = tenantInvites
        .filter((t) => t.tenantMethod === "self")
        .map((t) => ({ name: t.name, email: t.email }));
      const existingCoPending = selectedCoTenants
        .filter((u: any) => u?.type === "co_tenant")
        .map((u: any) => ({ name: u.name, email: u.email }));
      const allCoPending = [
        ...existingCoPending,
        ...pendingCoTenants.filter(
          (p) =>
            !existingCoPending.some((e) =>
              e.email
                ? e.email.toLowerCase() === p.email.toLowerCase()
                : e.name?.trim().toLowerCase() === p.name.trim().toLowerCase()
            )
        ),
      ];
      const applicationMembers: ApplicationMember[] = [
        {
          name:
            `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim() ||
            user?.email ||
            _t("you", "You"),
          email: user?.email,
          role: "tenant",
        },
        ...tenantInvites.map(({ name, email }) => ({
          name,
          email,
          role: "tenant" as const,
        })),
        ...guarantorInvites.map(({ name, email, guarantorMethod }) => ({
          name,
          email,
          role: "guarantor" as const,
          status:
            guarantorMethod === "self"
              ? ("pendingProfile" as const)
              : ("invitationSent" as const),
        })),
        ...existingGuarantors.map((guarantor: any) => ({
          name:
            guarantor?.gurantor_name ||
            `${guarantor?.guarantor_id?.fname ?? ""} ${guarantor?.guarantor_id?.lname ?? ""
              }`.trim() ||
            guarantor?.gurantor_email ||
            _t("guarantor", "Guarantor"),
          email: guarantor?.gurantor_email || guarantor?.guarantor_id?.email,
          role: "guarantor" as const,
        })),
      ];
      // If landlord invitations are pending, route to the landlord share choice
      // next instead of finishing (website cases 3-4, flatmate-link, and
      // landlord-link case 8). The queue transition is persisted synchronously
      // first – profile completion below remounts this step, and the remount
      // must restore the choice instead of the create form.
      console.log(flowTag, "create submitted", {
        createdAppId: createdAppId ?? null,
        tenantInvites: tenantInvites.length,
        landlordPending: (landlordRequests ?? []).length,
        returnToLandlordChoice,
      });
      if (useInvitationOrchestrator && (landlordRequests ?? []).length > 0) {
        const createdTitleForShare = title.trim() || defaultTitle;
        const newResolved = createdAppId
          ? {
            id: createdAppId,
            title: createdTitleForShare,
            leadEmail: user?.email ?? "",
            participants: [
              {
                name:
                  `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim() ||
                  user?.email,
                email: user?.email,
              },
              ...tenantInvites.map(({ name, email }) => ({ name, email })),
            ],
          }
          : null;
        const keepQueue = returnToLandlordChoice;
        const nextQueue = keepQueue
          ? landlordQueue
          : [...(landlordRequests ?? [])];
        persistQueueState(
          nextQueue,
          keepQueue ? landlordIndex : 0,
          newResolved,
          declinedFlatmate
        );
        await completeProfile(tenantUpdatePayload, clearQueueState);
        // State after completion (see completeProfile): avoids opening the
        // landlord choice twice across the remount.
        setResolvedApp(newResolved);
        if (keepQueue) {
          // Created from inside the landlord choice – stay on the current
          // landlord in the queue with the newly created application.
          setReturnToLandlordChoice(false);
        } else {
          setLandlordQueue(nextQueue);
          setLandlordIndex(0);
          setSharedLandlordNames([]);
        }
        return;
      }
      await completeProfile(tenantUpdatePayload);
      if (onCreated)
        onCreated({
          title: createdTitle,
          peopleCount,
          appId: createdAppId,
          members: applicationMembers,
          pendingGuarantors: allPending,
          pendingCoTenants: allCoPending,
        });
      else goToDashboardFromSignup("create finished: go to dashboard");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
        _t("failedToCreateApplication", "Failed to create application")
      );
    } finally {
      submittingRef.current = false;
      setCreating(false);
    }
  };

  // Guarantors added after joining or creating the application. A failure
  // here does not undo it: the tenant sees a toast and can add them later.
  const submitJoinGuarantors = async (
    invitesToSubmit: GuarantorInvite[],
    applicationId: string
  ) => {
    if (invitesToSubmit.length === 0) return;
    try {
      await submitGuarantors(invitesToSubmit, applicationId);
    } catch (err: any) {
      // account creation sends errorKey, the request email success_res.key
      const key =
        err?.response?.data?.errorKey ?? err?.response?.data?.success_res?.key;
      toast.error(
        key === "guarantor-already-in-application"
          ? _t("guarantorAlreadyInApplication")
          : _t("failedToAddGuarantor", "Failed to add guarantor")
      );
    }
  };

  const handleAcceptInvitation = async (
    joinGuarantorInvites: GuarantorInvite[],
    joinAs?: JoinAs
  ) => {
    if (!selectedApplication) return;
    console.log(flowTag, "flatmate decision: accept clicked", {
      appId: selectedApplication._id,
      title: selectedApplication.title,
    });
    setProcessingInvitation(true);
    try {
      // As a guarantor they join as the inviter's guarantor (the backend also
      // links their account); a guarantor brings no guarantors of their own
      const asGuarantor = joinAs === "guarantor";
      await consentApplication(
        selectedApplication._id,
        "accepted",
        asGuarantor ? "guarantor" : undefined
      );
      console.log(flowTag, "flatmate decision: consent accepted ok", {
        appId: selectedApplication._id,
        joinAs: joinAs ?? "flatmate",
      });
      if (!asGuarantor) {
        await submitJoinGuarantors(
          joinGuarantorInvites,
          selectedApplication._id
        );
      }
      // Profile completion remounts this step – each branch below persists its
      // transition (join success / landlord queue) BEFORE completing, so the
      // remount restores it instead of the create form.
      const profilePayload = {
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      };
      const joinedTitle = selectedApplication?.title || defaultTitle;
      // Website flow: already-shared application (case 2 accept) finishes here –
      // the profile reaches that landlord through the application linkage.
      // Not-shared with a landlord waiting (case 4 accept) goes to landlord choice.
      if (useWebsiteFlow) {
        const sharedNames = getSharedLandlordNames(selectedApplication);
        console.log(flowTag, "flatmate accept routing", {
          sharedNames,
          landlordPending: (landlordRequests ?? []).length,
        });
        if (sharedNames.length > 0) {
          console.log(
            flowTag,
            "flatmate accept: already shared, showing join success",
            {
              title: joinedTitle,
              sharedNames,
            }
          );
          persistJoinedResult({
            title: joinedTitle,
            sharedLandlords: sharedNames,
          });
          await completeProfile(profilePayload, clearJoinedResult);
          return;
        }
        if ((landlordRequests ?? []).length > 0) {
          const nextResolved = {
            id: selectedApplication._id,
            title: joinedTitle,
            participants: selectedApplication.participants,
            leadEmail: creatorEmailOf(selectedApplication),
          };
          const nextQueue = [...(landlordRequests ?? [])];
          persistQueueState(nextQueue, 0, nextResolved, false);
          await completeProfile(profilePayload, clearQueueState);
          // State after completion (see completeProfile): avoids opening the
          // landlord choice twice across the remount.
          setResolvedApp(nextResolved);
          setDeclinedFlatmate(false);
          setLandlordQueue(nextQueue);
          setLandlordIndex(0);
          setSharedLandlordNames([]);
          return;
        }
      }
      // Landlord-link entry (cases 9-11): partition the landlord queue per
      // landlord. Requests whose landlord already has the joined application
      // shared are auto-accepted silently (cases 10/11 – the linkage already
      // reaches them; the backend call is idempotent). The rest get the
      // landlord choice (case 9, case 11 remainder); closing that choice
      // shares via the link consent.
      if (useLandlordLinkFlow) {
        const sharedNames = getSharedLandlordNames(selectedApplication);
        const requests = [...(landlordRequests ?? [])];
        const requestLandlordId = (r: any) =>
          r?.landlord_id?._id ?? r?.landlord_id ?? null;
        const alreadyShared = requests.filter((r) => {
          const landlord = r?.landlord_id;
          const hasInsuranceConsent =
            haveValue(user?.consents?.gdpr_consent) &&
            haveValue(user?.consents?.skor_consent);
          const needsConsent =
            landlord?.insurance_service_status && !hasInsuranceConsent;
          // Route missing consent through the existing modal's consent gate.
          return (
            !needsConsent &&
            isAppSharedWithLandlordId(
              selectedApplication,
              requestLandlordId(r)?.toString?.()
            )
          );
        });
        const stillPending = requests.filter((r) => !alreadyShared.includes(r));
        const failedAuto: any[] = [];
        console.log(flowTag, "landlord-link accept routing", {
          sharedNames,
          landlordPending: requests.length,
          autoShared: alreadyShared.map((r: any) => r?._id ?? null),
          choicePending: stillPending.map((r: any) => r?._id ?? null),
        });
        for (const r of alreadyShared) {
          try {
            await acceptLandlordRequest(r._id, selectedApplication._id);
          } catch {
            // Keep failed requests in the choice queue so they can be retried.
            failedAuto.push(r);
          }
        }
        if (alreadyShared.length > 0) await loadLandlordRequests();
        const choiceQueue = [...stillPending, ...failedAuto];
        console.log(flowTag, "landlord-link auto-share done", {
          failedAuto: failedAuto.map((r: any) => r?._id ?? null),
        });
        if (choiceQueue.length > 0) {
          const nextResolved = {
            id: selectedApplication._id,
            title: joinedTitle,
            participants: selectedApplication.participants,
            leadEmail: creatorEmailOf(selectedApplication),
          };
          persistQueueState(choiceQueue, 0, nextResolved, false);
          await completeProfile(profilePayload, clearQueueState);
          // State after completion (see completeProfile): avoids opening the
          // landlord choice twice across the remount.
          setResolvedApp(nextResolved);
          setDeclinedFlatmate(false);
          setLandlordQueue(choiceQueue);
          setLandlordIndex(0);
          setSharedLandlordNames([]);
          return;
        }
        console.log(
          flowTag,
          "landlord-link accept: auto-shared, join success",
          {
            title: joinedTitle,
            sharedNames,
          }
        );
        persistJoinedResult({
          title: joinedTitle,
          sharedLandlords: sharedNames,
        });
        await completeProfile(profilePayload, clearJoinedResult);
        return;
      }
      await completeProfile(profilePayload);
      if (onCreated) onCreated({ title: joinedTitle, peopleCount });
      else
        goToDashboardFromSignup(
          "flatmate accept (no landlord): go to dashboard"
        );
    } catch (err: any) {
      // e.g. "you have a guarantor, so you cannot be one" when joining as one
      toast.error(
        err?.response?.data?.message ??
          _t("failedToJoinGroup", "Failed to join application")
      );
    } finally {
      setProcessingInvitation(false);
    }
  };

  const handleDeclineInvitation = async (
    joinGuarantorInvites: GuarantorInvite[]
  ) => {
    if (!selectedApplication) return;
    console.log(flowTag, "flatmate decision: decline clicked", {
      appId: selectedApplication._id,
      title: selectedApplication.title,
    });
    // Declining rejects the flatmate invitation and moves forward – it never
    // deletes the invitation record beyond the explicit reject, and closing
    // never deletes either. If a landlord is waiting, the landlord choice still
    // opens with the individual profile (website case 4, landlord-link case 9:
    // closing that choice then shares via the link consent).
    setProcessingInvitation(true);
    try {
      await submitGuarantors(joinGuarantorInvites);
      await consentApplication(selectedApplication._id, "rejected");
      console.log(flowTag, "flatmate decision: consent rejected ok", {
        appId: selectedApplication._id,
        landlordPending: (landlordRequests ?? []).length,
      });
      if (
        (useWebsiteFlow || useLandlordLinkFlow) &&
        (landlordRequests ?? []).length > 0
      ) {
        const nextQueue = [...(landlordRequests ?? [])];
        persistQueueState(nextQueue, 0, null, true);
        await completeProfile(
          {
            is_edit: false,
            profile_completed: true,
            role: localStorage.getItem("usertype"),
          },
          clearQueueState
        );
        // State after completion (see completeProfile): avoids opening the
        // landlord choice twice across the remount.
        setResolvedApp(null);
        setDeclinedFlatmate(true);
        setLandlordQueue(nextQueue);
        setLandlordIndex(0);
        setSharedLandlordNames([]);
        return;
      }
      setSelectedApplication(null);
      setDeclinedFlatmate(true);
    } catch {
      toast.error(_t("failedToDecline", "Failed to decline"));
    } finally {
      setProcessingInvitation(false);
    }
  };

  const handleFlatmateLinkFinish = async (
    joinGuarantorInvites: GuarantorInvite[]
  ) => {
    // Flatmate-link B-prime (cases 5-7): Finish-only, no Decline. The link
    // itself carries consent, so Finish accepts the pending application.
    // - Case 7 (already shared): no landlord modal – joining reaches the
    //   landlord through the application linkage, then join success.
    // - Case 6 (landlord waiting, not shared): route to landlord choice;
    //   closing that choice shares nothing.
    // - Case 5 (not shared, no landlord): join success without landlord line.
    if (!selectedApplication) return;
    console.log(flowTag, "flatmate-link: finish clicked", {
      appId: selectedApplication._id,
      title: selectedApplication.title,
    });
    setProcessingInvitation(true);
    try {
      const alreadyJoined = (myApplications ?? []).some(
        (app: any) => app?._id === selectedApplication._id
      );
      if (!alreadyJoined) {
        await consentApplication(selectedApplication._id, "accepted");
      }
      await submitJoinGuarantors(joinGuarantorInvites, selectedApplication._id);
      console.log(flowTag, "flatmate-link: consent accepted ok", {
        appId: selectedApplication._id,
      });
      // Profile completion remounts this step – persist the transition first.
      const profilePayload = {
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      };
      const joinedTitle = selectedApplication?.title || defaultTitle;
      const sharedNames = getSharedLandlordNames(selectedApplication);
      console.log(flowTag, "flatmate-link finish routing", {
        sharedNames,
        landlordPending: (landlordRequests ?? []).length,
      });
      if (sharedNames.length > 0) {
        console.log(flowTag, "flatmate-link: already shared, join success", {
          title: joinedTitle,
          sharedNames,
        });
        persistJoinedResult({
          title: joinedTitle,
          sharedLandlords: sharedNames,
        });
        await completeProfile(profilePayload, clearJoinedResult);
        return;
      }
      if ((landlordRequests ?? []).length > 0) {
        const nextResolved = {
          id: selectedApplication._id,
          title: joinedTitle,
          participants: selectedApplication.participants,
          leadEmail: creatorEmailOf(selectedApplication),
        };
        const nextQueue = [...(landlordRequests ?? [])];
        persistQueueState(nextQueue, 0, nextResolved, false);
        await completeProfile(profilePayload, clearQueueState);
        // State after completion (see completeProfile): avoids opening the
        // landlord choice twice across the remount.
        setResolvedApp(nextResolved);
        setDeclinedFlatmate(false);
        setLandlordQueue(nextQueue);
        setLandlordIndex(0);
        setSharedLandlordNames([]);
        return;
      }
      persistJoinedResult({ title: joinedTitle, sharedLandlords: [] });
      await completeProfile(profilePayload, clearJoinedResult);
    } catch {
      toast.error(_t("failedToJoinGroup", "Failed to join application"));
    } finally {
      setProcessingInvitation(false);
    }
  };

  const landlordDisplayName = (request: any) => {
    const landlord = request?.landlord_id ?? {};
    return (
      `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
      landlord.email ||
      _t("landlord", "Landlord")
    );
  };

  // Creator email for lead-tenant detection (created_by is populated with
  // fname/lname/email on the pending-application API, but may be a bare id).
  const creatorEmailOf = (app: any) => {
    const creator = app?.created_by;
    return creator && typeof creator === "object" ? (creator.email ?? "") : "";
  };

  // Member rows for the shared success screen: lead first, you second, then
  // the rest (same ranking as the join screen). Lead is the is_lead
  // participant, else the creator, else the first participant.
  const buildShareMembers = (): SharedMember[] => {
    const parts = (resolvedApp?.participants ?? []).filter(
      (p: any) => p && !p.is_deleted && (p.email || p.name)
    );
    const me = (user?.email ?? "").toLowerCase();
    const leadEmail = (resolvedApp?.leadEmail ?? "").toLowerCase();
    const hasKnownLead = parts.some(
      (p: any) =>
        p.is_lead ||
        (leadEmail !== "" && (p.email ?? "").toLowerCase() === leadEmail)
    );
    let rows: SharedMember[] = parts.map((p: any, index: number) => {
      const email = p.email ?? "";
      return {
        name: p.name || email.split("@")[0] || "",
        email,
        isLead: hasKnownLead
          ? !!p.is_lead ||
          (leadEmail !== "" && email.toLowerCase() === leadEmail)
          : index === 0,
      };
    });
    if (me !== "" && !rows.some((r) => r.email.toLowerCase() === me)) {
      const fallbackName =
        `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim() || me.split("@")[0];
      rows = [
        ...rows,
        {
          name: fallbackName,
          email: user?.email ?? "",
          isLead: rows.length === 0 && !hasKnownLead,
        },
      ];
    }
    const rank = (row: SharedMember) =>
      row.isLead ? 0 : row.email.toLowerCase() === me && me !== "" ? 1 : 2;
    return [...rows].sort((a, b) => rank(a) - rank(b));
  };

  const handleLandlordShared = () => {
    // Successful share inside the reused landlord share modal – advance to the
    // next waiting landlord, or finish when the queue is done.
    const current = landlordQueue[landlordIndex];
    const sharedName = landlordDisplayName(current);
    const nextNames = [...sharedLandlordNames, sharedName].filter(
      (n, i, arr) => n && arr.indexOf(n) === i
    );
    setSharedLandlordNames(nextNames);
    console.log(flowTag, "landlord choice: shared", {
      requestId: current?._id ?? null,
      appId: resolvedApp?.id ?? null,
      index: landlordIndex,
      total: landlordQueue.length,
      sharedName,
    });
    const next = landlordIndex + 1;
    if (next < landlordQueue.length) {
      setLandlordIndex(next);
      persistQueueState(landlordQueue, next, resolvedApp, declinedFlatmate);
    } else {
      finishLandlordQueue(nextNames);
    }
    // Refresh the tenant only if the identification form was just submitted
    // (consent may have flipped to accepted) so the next queue item and
    // downstream steps see fresh status. The queue transition above is already
    // persisted, so a remount restores correctly.
    if (viviaSubmittedRef.current) {
      viviaSubmittedRef.current = false;
      console.log(flowTag, "landlord choice: reloading user after share", {
        requestId: current?._id ?? null,
        index: landlordIndex,
        total: landlordQueue.length,
      });
      loadUserTenant("tenant").catch(() => {
        console.log(
          flowTag,
          "landlord choice: user reload failed after share",
          {
            requestId: current?._id ?? null,
          }
        );
      });
    }
  };

  const handleLandlordClose = () => {
    // Closing shares nothing – the invitation stays pending for the red dot in
    // Share profile, and the modal does not reopen on its own. With several
    // landlords waiting, closing one advances to the next.
    const current = landlordQueue[landlordIndex];
    console.log(flowTag, "landlord choice: closed (shares nothing)", {
      requestId: current?._id ?? null,
      index: landlordIndex,
      total: landlordQueue.length,
    });
    const next = landlordIndex + 1;
    if (next < landlordQueue.length) {
      setLandlordIndex(next);
      persistQueueState(landlordQueue, next, resolvedApp, declinedFlatmate);
    } else {
      finishLandlordQueue();
    }
  };

  const finishLandlordQueue = (sharedNamesOverride?: string[]) => {
    const sharedNames = sharedNamesOverride ?? sharedLandlordNames;
    console.log(flowTag, "landlord queue finished", {
      sharedAppId: resolvedApp?.id ?? null,
      declinedFlatmate,
      sharedNames,
    });
    clearQueueState();
    setLandlordQueue([]);
    setLandlordIndex(0);
    setReturnToLandlordChoice(false);
    if (sharedNames.length > 0) {
      // At least one landlord was shared: show the shared confirmation instead
      // of going straight home.
      const tenantLabel =
        `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim() ||
        user?.email ||
        _t("yourApplication", "your application");
      persistSharedResult({
        landlordNames: sharedNames,
        tenantLabel,
        tenantEmail: user?.email ?? "",
        appTitle: resolvedApp?.title ?? null,
        members: buildShareMembers(),
      });
      return;
    }
    if (resolvedApp?.id) {
      if (onCreated)
        onCreated({
          title: resolvedApp.title,
          peopleCount,
          appId: resolvedApp.id,
        });
      else
        goToDashboardFromSignup(
          "landlord queue finished (app): go to dashboard"
        );
    } else if (declinedFlatmate || !selectedApplication) {
      // Individual profile path – profile stays, dashboard shows red dot.
      goToDashboardFromSignup(
        "landlord queue finished (individual): go to dashboard"
      );
    } else {
      goToDashboardFromSignup(
        "landlord queue finished (fallback): go to dashboard"
      );
    }
  };

  const handleLandlordCreateNew = () => {
    // Landlord choice "Create a new Application": show the create form, then
    // return to the remaining landlord queue with the newly created app.
    console.log(flowTag, "landlord choice: create new application selected");
    setReturnToLandlordChoice(true);
    setResolvedApp(null);
  };

  const renderLandlordChoice = () => {
    const current = landlordQueue[landlordIndex];
    if (!current) return null;
    // Closing the choice shares nothing on website / flatmate-link entries
    // (cases 3-4, 6) but shares via the link consent on landlord-link entry
    // (cases 8-11).
    const choiceCloseBehavior = useLandlordLinkFlow ? "share" : "nothing";
    const choiceWorkspaceRef = current?.workspace_id;
    console.log(flowTag, "landlord choice shown", {
      requestId: current?._id,
      landlord:
        `${current?.landlord_id?.fname_companyName ?? ""} ${current?.landlord_id?.lname ?? ""}`.trim() ||
        current?.landlord_id?.email ||
        null,
      index: landlordIndex,
      total: landlordQueue.length,
      primaryAppId: resolvedApp?.id ?? null,
      closeBehavior: choiceCloseBehavior,
      clientIdentificationRule: {
        request: isClientIdentificationActive(current),
        landlord: isClientIdentificationActive(current?.landlord_id),
        workspace:
          choiceWorkspaceRef && typeof choiceWorkspaceRef === "object"
            ? isClientIdentificationActive(choiceWorkspaceRef)
            : null,
      },
    });
    // Reuse the existing landlord share modal in API-driven mode so choice copy,
    // consent gating, and share logic stay consistent everywhere.
    return (
      <>
        <InvitationShareModal
          key={current._id}
          isOpen={!consentLandlordData}
          landlordId={null}
          propertyId={null}
          requestOverride={current}
          applicationOverride={
            resolvedApp?.id
              ? {
                _id: resolvedApp.id,
                title: resolvedApp.title,
                participants: resolvedApp.participants ?? [],
              }
              : null
          }
          closeBehavior={choiceCloseBehavior}
          onCreateNew={handleLandlordCreateNew}
          onClose={handleLandlordClose}
          onClearCookies={() => { }}
          onNeedsConsent={(landlordData: any, action: () => Promise<void>) => {
            postConsentActionRef.current = action;
            setConsentLandlordData(landlordData);
          }}
          onDone={handleLandlordShared}
          landlordDataOverride={undefined}
          compactChoice={false}
          enableViviaGate
          onViviaSubmitted={() => {
            viviaSubmittedRef.current = true;
          }}
        />
        {consentLandlordData && (
          <ConsentStepModal
            key={current._id}
            user={user}
            isPopup
            updateTenantData={updateTenantData}
            originalUser={user}
            loadSharedProfiles={undefined}
            isJoinedFromLandlordLink={false}
            selectedLandlord={consentLandlordData}
            loadUserTenant={loadUserTenant}
            shareProfileWithLandlord={async () => {
              const action = postConsentActionRef.current;
              if (action) await action();
              postConsentActionRef.current = null;
            }}
            onClose={() => {
              postConsentActionRef.current = null;
              setConsentLandlordData(null);
            }}
          />
        )}
      </>
    );
  };

  const showLandlordQueue =
    useInvitationOrchestrator &&
    landlordQueue.length > 0 &&
    !returnToLandlordChoice;

  if (initializationError && !initializing) {
    const errorKey = "failedToLoadSignupInvitations";
    const errorFallback =
      "We couldn't load your invitations. Please try again.";
    const errorMessage = _t(errorKey, errorFallback);
    const retryLabel = _t("tryAgain", "Try again");
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <main className={`${styles.card} onboarding-modal-surface`}>
          <p role="alert">
            {errorMessage === errorKey ? errorFallback : errorMessage}
          </p>
          <footer className={styles.footer}>
            <Button
              variant="primary"
              size="lg"
              className={styles.createButton}
              onClick={() => {
                setInitializing(true);
                setInitializationAttempt((attempt) => attempt + 1);
              }}
            >
              {retryLabel === "tryAgain" ? "Try again" : retryLabel}
            </Button>
          </footer>
        </main>
      </div>
    );
  }

  if (!applicationsLoaded || initializing || creating) {
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <img src={SkorDefaultLoader} className={styles.loader} alt="" />
      </div>
    );
  }

  // Preserve the pending-profile follow-up before the flatmate join result.
  if (useFlatmateLinkFlow && joinedResult) {
    const pendingProfiles = renderPendingProfiles();
    if (pendingProfiles) return pendingProfiles;
  }

  if (joinedResult) {
    const landlordLine =
      joinedResult.sharedLandlords.length > 0
        ? _t(
          "joinedAppReachesLandlords",
          "Your profile now reaches {landlords}.",
          undefined,
          { landlords: joinedResult.sharedLandlords.join(", ") }
        )
        : null;
    // Fallback when the translation key is missing: _t returns the key itself.
    const reachesText =
      landlordLine && !landlordLine.includes("joinedAppReachesLandlords")
        ? landlordLine
        : `Your profile now reaches ${joinedResult.sharedLandlords.join(", ")}.`;
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <main className={`${styles.card} onboarding-modal-surface`}>
          <h1>
            {_t("youAreIn", "You're in")} {joinedResult.title}
          </h1>
          <p className={styles.intro}>
            {_t(
              "profileNowPartOfApp",
              "Your profile is now part of this application."
            )}
          </p>
          {joinedResult.sharedLandlords.length > 0 && <p>{reachesText}</p>}
          <footer className={styles.footer}>
            <Button
              variant="primary"
              size="lg"
              className={styles.createButton}
              onClick={() => {
                goToDashboardFromSignup("join success: go to dashboard");
              }}
            >
              {_t("goToMyDashboard", "Go to my dashboard")}
            </Button>
          </footer>
        </main>
      </div>
    );
  }

  if (sharedResult) {
    const names = sharedResult.landlordNames.join(", ");
    const goToDashboard = () =>
      goToDashboardFromSignup("shared success: go to dashboard", { names });
    // One component for both cases: application shares show the property
    // line and the email bell line; individual-profile shares (no
    // application) keep their tenant line and hide the bell line.
    const isAppShare = !!sharedResult.appTitle;
    return (
      <SharedWithLandlordScreen
        names={names}
        appTitle={sharedResult.appTitle}
        members={sharedResult.members ?? []}
        userEmail={user?.email ?? ""}
        onDashboard={goToDashboard}
        _t={_t}
        tenantLabel={isAppShare ? null : sharedResult.tenantLabel}
        showBellLine={isAppShare}
      />
    );
  }

  // The landlord choice is a complete screen, not a layer over the create form.
  if (showLandlordQueue) return renderLandlordChoice();

  if (selectedApplication && (useWebsiteFlow || useLandlordLinkFlow)) {
    // Flatmate invitation decision with explicit accept/decline and an
    // Already-shared chip when the application is already shared:
    // website cases 2 and 4, landlord-link cases 9, 10, 11. The accept/decline
    // handlers branch on entry source for routing (auto-share vs choice,
    // X shares nothing vs via link consent).
    const sharedNames = getSharedLandlordNames(selectedApplication);
    return (
      <>
        <div className={`${styles.overlay} modal-fixed-screen`}>
          <JoinApplicationScreen
            app={selectedApplication}
            onAccept={handleAcceptInvitation}
            onDecline={handleDeclineInvitation}
            onBack={onBack}
            processing={processingInvitation}
            user={user}
            _t={_t}
            sharedLandlordNames={sharedNames}
          />
        </div>
      </>
    );
  }

  if (selectedApplication && useFlatmateLinkFlow) {
    // Flatmate-link B-prime (cases 5-7): Finish-only, no Decline. Already-
    // shared chip still shown so case 7 is explicit about where Finish lands.
    const sharedNames = getSharedLandlordNames(selectedApplication);
    return (
      <>
        <div className={`${styles.overlay} modal-fixed-screen`}>
          <JoinApplicationScreen
            app={selectedApplication}
            onAccept={handleFlatmateLinkFinish}
            onBack={onBack}
            processing={processingInvitation}
            user={user}
            _t={_t}
            sharedLandlordNames={sharedNames}
          />
        </div>
      </>
    );
  }

  if (selectedApplication) {
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <JoinApplicationScreen
          app={selectedApplication}
          onAccept={handleAcceptInvitation}
          onBack={onBack}
          processing={processingInvitation}
          user={user}
          _t={_t}
        />
      </div>
    );
  }

  function renderPendingProfiles() {
    if (!showCoTenantPending && !showPendingOnRefresh) return null;
    // Build co-tenant pending
    const buildCoPending = () => {
      const selectedRaw = selectedCoTenants.filter(
        (u: any) => u?.type === "co_tenant"
      ) as any[];
      const completedUsers: any[] =
        user?.tenant_extra_field_id?.completed_co_tenants ?? [];
      const normEmail = (v: any) =>
        typeof v === "string" ? v.toLowerCase() : "";
      const normName = (v: any) =>
        typeof v === "string" ? v.trim().toLowerCase() : "";
      const keyOf = (g: any) => normEmail(g.email) || normName(g.name);
      const mergedMap = new Map<string, any>();
      for (const g of selectedRaw) {
        const k = keyOf(g);
        if (k && !mergedMap.has(k)) mergedMap.set(k, g);
      }
      for (const c of completedUsers) {
        const k = keyOf(c);
        if (k && !mergedMap.has(k))
          mergedMap.set(k, { name: c.name, email: c.email, type: "co_tenant" });
      }
      const selected = Array.from(mergedMap.values());
      return selected.map((p: any) => {
        const email = normEmail(p.email);
        const name = normName(p.name);
        let completed = false;
        if (
          email &&
          completedUsers.some((c: any) => normEmail(c?.email) === email)
        )
          completed = true;
        if (
          !completed &&
          name &&
          completedUsers.some((c: any) => normName(c?.name) === name)
        )
          completed = true;
        return { ...p, completed, type: "co_tenant" };
      });
    };
    const buildGuarPending = () => {
      const selectedRaw = selectedOnboardingUsers.filter(
        (u: any) => u?.type === "guarantor"
      ) as any[];
      const added = user?.gurantor_added_id ?? [];
      const completedUsers: any[] =
        user?.tenant_extra_field_id?.completed_onboarding_users ?? [];
      const normEmail = (v: any) =>
        typeof v === "string" ? v.toLowerCase() : "";
      const normName = (v: any) =>
        typeof v === "string" ? v.trim().toLowerCase() : "";
      const keyOf = (g: any) => normEmail(g.email) || normName(g.name);
      const mergedMap = new Map<string, any>();
      for (const g of selectedRaw) {
        const k = keyOf(g);
        if (k && !mergedMap.has(k)) mergedMap.set(k, g);
      }
      for (const c of completedUsers.filter(
        (c: any) => c?.type === "guarantor"
      )) {
        const k = keyOf(c);
        if (k && !mergedMap.has(k))
          mergedMap.set(k, { name: c.name, email: c.email, type: "guarantor" });
      }
      const selected = Array.from(mergedMap.values());
      return selected.map((p: any) => {
        const email = normEmail(p.email);
        const name = normName(p.name);
        let completed = false;
        if (email) {
          if (
            completedUsers.some(
              (c: any) =>
                c?.type === "guarantor" && normEmail(c?.email) === email
            )
          )
            completed = true;
          else if (
            added.some(
              (a: any) =>
                normEmail(a?.gurantor_email) === email ||
                normEmail(a?.guarantor_id?.email) === email
            )
          )
            completed = true;
        }
        if (!completed && name) {
          if (
            completedUsers.some(
              (c: any) => c?.type === "guarantor" && normName(c?.name) === name
            )
          )
            completed = true;
          else if (
            added.some((a: any) => {
              const an = normName(
                `${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`.trim()
              );
              return !!an && an === name;
            })
          )
            completed = true;
        }
        return { ...p, completed, type: "guarantor" };
      });
    };
    const coPending = buildCoPending();
    const guarPending = buildGuarPending();
    const combined = [...guarPending, ...coPending];
    const hasAnyPending = combined.some((x) => !x.completed);
    if (!hasAnyPending && combined.length === 0) {
      // nothing to show, clear flags
    } else if (combined.length > 0 && (!joinedResult || hasAnyPending)) {
      return (
        <div className={`${styles.overlay} modal-fixed-screen`}>
          <SelfManagedGuarantorsModal
            guarantors={combined as any}
            onLater={() => {
              setShowPendingOnRefresh(false);
              setShowCoTenantPending(false);
            }}
            inline
          />
        </div>
      );
    }
    return null;
  }

  // Never fall back to the create form during an invitation transition.
  if (processingInvitation) {
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <img src={SkorDefaultLoader} className={styles.loader} alt="" />
      </div>
    );
  }

  const pendingProfiles = renderPendingProfiles();
  if (pendingProfiles) return pendingProfiles;

  return (
    <div
      className={`${styles.overlay} modal-fixed-screen onboarding-application-step`}
    >
      <main className={`${styles.card} onboarding-modal-surface`}>
        <div className={styles.body}>
          <Button
            variant="ghost"
            size="md"
            className={styles.backButton}
            iconStart={<Icon name="chevronLeft" size={16} />}
            onClick={onBack}
          >
            {_t("back", "Back")}
          </Button>

          <h1>{_t("createYourApplication", "Create your application")}</h1>

          <div className={styles.titleField}>
            <TextField
              id="application-title"
              label={_t("applicationName", "Application name")}
              ref={titleInputRef}
              value={title}
              readOnly={!editingTitle}
              maxLength={80}
              onChange={(event) => {
                titleChangedRef.current = true;
                setTitle(event.target.value);
              }}
              onBlur={() => {
                setTitle((current) => {
                  if (current.trim()) return current.trim();
                  titleChangedRef.current = false;
                  return defaultTitle;
                });
                setEditingTitle(false);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
              }}
              trailing={
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  iconStart={<Icon name="edit" size={16} />}
                  onClick={() => {
                    setEditingTitle(true);
                    requestAnimationFrame(() => titleInputRef.current?.focus());
                  }}
                  aria-label={_t(
                    "editApplicationName",
                    "Edit application name"
                  )}
                />
              }
            />
          </div>

          <p className={styles.intro}>
            {_t(
              "addPeopleToRentalApplication",
              "Add the people who'll be part of your rental. Each one gets an email invitation, or you can fill in their profile."
            )}
          </p>

          <section
            className={styles.people}
            aria-label={_t("people", "People")}
          >
            <div className={styles.personRow}>
              <Avatar name={user?.fname || user?.email || "Y"} />
              <div>
                <div className={styles.personName}>
                  <strong>{_t("you", "You")}</strong>
                  <Badge tone="neutral">
                    {_t("leadTenant", "Lead tenant")}
                  </Badge>
                </div>
                <p>{user?.email}</p>
              </div>
            </div>

            {(
              selectedOnboardingUsers.filter(
                (u: any) => u?.type === "guarantor"
              ) as any[]
            ).map((g: any) => (
              <div
                className={`${styles.addedPerson} ${styles.addedPersonIndented}`}
                key={g.email || g.name}
              >
                <Avatar name={g.name || g.email || "G"} />
                <div>
                  <strong>{g.name}</strong>
                  <p>
                    {g.email} · {_t("guarantor", "Guarantor")}
                  </p>
                </div>
              </div>
            ))}
            <GuarantorInviteSection
              currentUserEmail={user?.email}
              invites={guarantorInvites}
              onChange={setGuarantorInvites}
              _t={_t}
            />

            <div className={styles.tenantDivider} />

            {(
              selectedCoTenants.filter(
                (u: any) => u?.type === "co_tenant"
              ) as any[]
            ).map((g: any) => (
              <div className={styles.addedPerson} key={g.email || g.name}>
                <Avatar name={g.name || g.email || "G"} />
                <div>
                  <strong>{g.name}</strong>
                  <p>
                    {g.email} · {_t("co_tenant", "Co-tenant")}
                  </p>
                </div>
              </div>
            ))}
            <TenantInviteSection
              currentUserEmail={user?.email}
              invites={tenantInvites}
              onChange={setTenantInvites}
              _t={_t}
            />
          </section>

          <div className={styles.noteSection}>
            <div className={styles.noteHeader}>
              <span>
                {_t(
                  "addANoteAboutHousehold",
                  "Add a note about your household (optional)"
                )}
              </span>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                iconStart={<Icon name="chevronDown" size={18} />}
                className={showNote ? styles.noteToggleOpen : ""}
                onClick={() => setShowNote((current) => !current)}
                aria-expanded={showNote}
                aria-controls="application-household-note"
                aria-label={
                  showNote ? _t("collapse", "Collapse") : _t("expand", "Expand")
                }
              />
            </div>
            {showNote && (
              <TextArea
                id="application-household-note"
                value={description}
                maxLength={500}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={_t(
                  "applicationDescriptionExample",
                  "E.g. We're a couple with permanent contracts, relocating for work in March."
                )}
                aria-label={_t(
                  "applicationDescription",
                  "Application description"
                )}
              />
            )}
          </div>

          <Checkbox
            className={`${styles.protectCard} ${skorProtect ? styles.protectCardActive : ""}`}
            checked={skorProtect}
            onChange={setSkorProtect}
            label={
              <div className={styles.protectContent}>
                <div className={styles.protectHeading}>
                  <span className={styles.shield}>
                    <img src={SkorProtectShield} alt="" />
                  </span>
                  <strong>{_t("skorProtectTitle", "Skor Protect")}</strong>
                  <Badge tone="accent">
                    {_t("recommended", "Recommended")}
                  </Badge>
                </div>
                <p>
                  {_t(
                    "skorProtectDesc",
                    "Signal to landlords that rent is backed by Skor, boosting your chances of getting the property."
                  )}
                </p>
                <ul>
                  <li>
                    <Icon name="check" size={14} />
                    <span>
                      <strong>
                        {_t("skorBenefit1Bold", "Landlords trust you more")}
                      </strong>{" "}
                      - {_t("skorBenefit1Rest", "rent is backed")}
                    </span>
                  </li>
                  <li>
                    <Icon name="check" size={14} />
                    <span>
                      <strong>
                        {_t("skorBenefit2Bold", "You're covered")}
                      </strong>{" "}
                      -{" "}
                      {_t(
                        "skorBenefit2Rest",
                        "if a dispute with your landlord arises we help you"
                      )}
                    </span>
                  </li>
                  <li>
                    <Icon name="check" size={14} />
                    <span>
                      <strong>
                        {_t("skorBenefit3Bold", "Stronger candidacy")}
                      </strong>{" "}
                      -{" "}
                      {_t("skorBenefit3Rest", "visible on your shared profile")}
                    </span>
                  </li>
                </ul>
                <p className={styles.pricing}>
                  {_t("skorNoChargeNow", "No charge now")} ·{" "}
                  <u>
                    {_t(
                      "skorPriceConfirmed",
                      "Price confirmed once the rental amount is set"
                    )}
                  </u>
                </p>
              </div>
            }
          />
        </div>

        <footer className={styles.footer}>
          <p>
            {peopleCount}{" "}
            {peopleCount === 1
              ? _t("person", "person")
              : _t("people", "people")}{" "}
            {_t("inThisApplication", "in this application")}.
          </p>
          <Button
            variant="primary"
            size="lg"
            className={styles.createButton}
            onClick={handleCreate}
            loading={creating}
          >
            {_t("createApplication", "Create application")}
          </Button>
        </footer>
      </main>
    </div>
  );
};

export default CreateApplicationStep;
